import { unaccounted, type RasterLedger } from '@drawing2d/core';
import { t, fill, type Lang } from '../i18n.js';

/**
 * The ink stamp — the same accounting, over a unit the other stamp never counts.
 *
 * `Ledger.tsx` carries the sentence that separates this product from every other
 * converter: every operator in the PDF became an entity or was recorded, and
 * nothing is unaccounted. A scan has no operators to make that claim about. It
 * has ink, so this counts ink:
 *
 *     explained + recorded === total ink pixels
 *
 * **The wording is its own and not the vector stamp's, and that is the whole
 * point of the component existing.** The two stamps are visually identical by
 * design — the accounting is the same accounting — which is exactly why a reader
 * who has met the first one would otherwise read `27,431` here as a count of
 * operators. It is a count of pixels on a sheet, an order of magnitude that means
 * something entirely different, and only the words can say so. Different unit,
 * different copy, in both languages.
 *
 * The `data-figure` attribute names are shared with the vector stamp on purpose:
 * they are the account's roles — what came in, what was explained, what was
 * recorded, what is missing — and a test that checks an account reconciles should
 * not have to know which surface it is standing on.
 *
 * **What it must not be read as is a score.** The figures say how much ink a
 * detection stood on, not how much of the drawing was recognised correctly: a
 * detector reporting lines all over the page would explain more ink and be worse.
 * So the explained fraction is printed with the sentence saying it is not a bar,
 * and the note says plainly what this detector does not attempt at all.
 */
export function InkLedger({
  ledger,
  lang,
}: {
  ledger: RasterLedger;
  lang: Lang;
}): JSX.Element {
  const located = ledger.located.reduce((sum, region) => sum + region.pixels, 0);
  // Located and diffuse are one figure on screen and two in the record. Both are
  // ink no detection explained; what separates them is only whether a bounding
  // box around it would have told the reader anything.
  const recorded = located + ledger.diffuse.pixels;
  const missing = unaccounted(ledger);
  const reconciled = missing === 0;

  // Six and seven digits are ordinary here — an A4 sheet at 600 DPI is 34.8 M
  // pixels — and an unseparated 1027431 is not a number anyone reads. Grouped
  // the same way in both languages, because the grouping is a property of the
  // digits and Japanese engineering documents group them identically.
  const figure = (n: number): string => n.toLocaleString('en-US');
  const explainedFraction = ledger.totalInk === 0 ? null : ledger.explained / ledger.totalInk;

  return (
    <section
      className={`stamp${reconciled ? ' reconciled' : ' short'}`}
      data-ink-ledger=""
      data-reconciled={reconciled}
    >
      {/* An h2 rather than the h3 its neighbours use: the heading is part of the
          stamp's own type, and the stamp reads the same on both surfaces. */}
      <h2>{t('inkLedger', lang)}</h2>

      <dl className="stamp-figures">
        <div>
          <dt>{t('inkTotal', lang)}</dt>
          <dd data-figure="total">{figure(ledger.totalInk)}</dd>
        </div>
        <div>
          {/* `consumed` is the role, not the word on screen. Ink is never
              consumed by anything — it is explained by a band lying over it — but
              the account's second column is the same column on both stamps. */}
          <dt>{t('inkExplained', lang)}</dt>
          <dd data-figure="consumed">{figure(ledger.explained)}</dd>
        </div>
        <div>
          <dt>{t('inkRecorded', lang)}</dt>
          <dd data-figure="recorded">{figure(recorded)}</dd>
        </div>
        <div className="stamp-claim">
          <dt>{t('inkUnaccounted', lang)}</dt>
          <dd data-figure="unaccounted" className={reconciled ? 'ok' : 'bad'}>
            {figure(missing)}
          </dd>
        </div>
      </dl>

      {/*
        One note, because the vector stamp has one and this surface is held to
        two screens. A percentage rather than the three-decimal form a confidence
        takes: three decimals exist to stop a detector claiming precision it does
        not have, and this is a share of a counted quantity whose two operands
        are exact and printed directly above it.
      */}
      {explainedFraction === null ? (
        // Nothing was explained because there was nothing to explain. Printing
        // "0.0%" here would present an undefined ratio as a measurement, on the
        // one surface whose whole job is not to overstate -- and 0/0 is the
        // easiest number in the world to publish by accident.
        //
        // Its own string, not the first sentence of the one below. That was
        // `split('.')`, and Japanese ends a sentence with a different character
        // entirely, so a Japanese reader got both sentences and the literal
        // text `{fraction}` where a number should have been.
        <p className="note">{t('inkLedgerNothing', lang)}</p>
      ) : (
        <p className="note" data-ink-explained={explainedFraction.toFixed(3)}>
          {fill(t('inkLedgerExplain', lang), {
            fraction: `${(explainedFraction * 100).toFixed(1)}%`,
          })}
        </p>
      )}
    </section>
  );
}
